import {
  IconButton as ChakraIconButton,
  IconButtonProps as ChakraIconButtonProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import type { ReactElement, ReactNode } from "react";
import Icon from "@ui-kit/icons/Icon.web";
import { IconButtonSharedProps } from "./IconButton.props";
import { getColorStyles } from "../Button/Button.web";

// Check if a value is an Emotion SerializedStyles object
function isEmotionStyles(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === "object" &&
    "styles" in value &&
    "name" in value
  );
}

// Transform Emotion SerializedStyles to Chakra-compatible format
function processStyleValue(value: unknown): unknown {
  if (!value) return value;

  if (isEmotionStyles(value)) {
    const { styles } = value as { styles: string };
    return { "&": styles };
  }

  if (Array.isArray(value)) {
    return value.map(processStyleValue);
  }

  return value;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      colorPalette,
      variant = "plain",
      iconName,
      iconColor,
      iconSize,
      iconOutline = true,
      _disabled,
      _hover,
      hoverColor,
      isDisabled,
      disabled,
      isLoading,
      loading,
      isRound,
      icon,
      children,
      sx,
      css,
      ...props
    }: IconButtonProps,
    ref
  ): JSX.Element {
    // When no iconColor is provided, use colorPalette for two-tone icon styling
    // We need to explicitly set color to override the parent button's color inheritance
    const useColorPaletteForIcon = !iconColor && colorPalette;

    function getIcon(iconName: string, iconColor?: string): JSX.Element {
      // When using colorPalette, set color to the 900 shade to match createWebIcon's default
      // This overrides the inherited color from the parent button
      const effectiveIconColor =
        iconColor ||
        (useColorPaletteForIcon ? `${colorPalette}.900` : undefined);

      return (
        <Icon
          outline={iconOutline}
          name={iconName}
          w={iconSize ?? "1em"}
          h={iconSize ?? "1em"}
          colorPalette={colorPalette}
          color={effectiveIconColor}
        />
      );
    }

    const disabledValue = disabled !== undefined ? disabled : isDisabled;
    const loadingValue = loading !== undefined ? loading : isLoading;
    const colorSchemeValue = colorPalette ?? "primary";
    const borderRadiusValue = isRound ? "full" : props.borderRadius;
    const unstyledValue = variant === "unstyled";
    const chakraVariant =
      variant === "unstyled"
        ? undefined
        : (variant as ChakraIconButtonProps["variant"]);

    // Transform Emotion SerializedStyles to Chakra format
    const processedCss = processStyleValue(css);
    const processedSx = processStyleValue(sx);

    const mergedCss =
      processedSx && processedCss
        ? ([
            processedCss,
            processedSx,
          ] as unknown as ChakraIconButtonProps["css"])
        : processedSx ?? processedCss;

    // Get color styles from Button's getColorStyles for consistent theming
    const variantString = typeof variant === "string" ? variant : "solid";
    const colorString = typeof iconColor === "string" ? iconColor : undefined;
    const colorStyles = getColorStyles(
      variantString,
      colorSchemeValue,
      loadingValue,
      colorString
    ) as ChakraIconButtonProps;

    return (
      <ChakraIconButton
        ref={ref}
        unstyled={unstyledValue}
        variant={chakraVariant ?? "ghost"}
        disabled={disabledValue}
        loading={loadingValue}
        borderRadius={borderRadiusValue}
        css={mergedCss as ChakraIconButtonProps["css"]}
        bg={colorStyles.bg}
        color={colorStyles.color}
        display="flex"
        alignItems="center"
        justifyContent="center"
        // eslint-disable-next-line no-underscore-dangle
        _disabled={{ ...colorStyles._disabled, ..._disabled }}
        // eslint-disable-next-line no-underscore-dangle
        _active={colorStyles._active}
        // eslint-disable-next-line no-underscore-dangle
        _hover={{
          // Apply default color styles first
          // eslint-disable-next-line no-underscore-dangle
          ...colorStyles._hover,
          // Changes the icon color when the button is hovered (only if color specified)
          ...(hoverColor || iconColor
            ? { "& > *": { color: hoverColor || iconColor } }
            : {}),
          // User-provided _hover overrides defaults (applied last)
          // eslint-disable-next-line no-underscore-dangle
          ..._hover,
        }}
        {...props}
      >
        {icon ??
          children ??
          getIcon(
            iconName,
            disabledValue && _disabled?.color
              ? _disabled.color.toString()
              : iconColor
          )}
      </ChakraIconButton>
    );
  }
) as React.ForwardRefExoticComponent<
  IconButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Solid: typeof Solid;
  Outline: typeof Outline;
  Ghost: typeof Ghost;
  Primitive: typeof Primitive;
};

const Solid = forwardRef<HTMLButtonElement, IconButtonProps>(function Solid(
  props,
  ref
) {
  return <IconButton ref={ref} variant="solid" {...props} />;
});

const Outline = forwardRef<HTMLButtonElement, IconButtonProps>(function Outline(
  props,
  ref
) {
  return <IconButton ref={ref} variant="outline" {...props} />;
});

const Ghost = forwardRef<HTMLButtonElement, IconButtonProps>(function Ghost(
  props,
  ref
) {
  return (
    <IconButton ref={ref} variant="ghost" colorPalette="ghost" {...props} />
  );
});

type PrimitiveIconButtonProps = Omit<
  ChakraIconButtonProps,
  "children" | "variant"
> & {
  icon?: ReactElement;
  children?: ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  isRound?: boolean;
  variant?:
    | "solid"
    | "outline"
    | "ghost"
    | "unstyled"
    | "subtle"
    | "surface"
    | "plain";
};

const Primitive = forwardRef<HTMLButtonElement, PrimitiveIconButtonProps>(
  function Primitive(
    {
      icon,
      children,
      isLoading,
      isDisabled,
      isRound,
      variant = "plain",
      ...props
    },
    ref
  ) {
    const borderRadiusValue = isRound ? "full" : props.borderRadius;
    const unstyledValue = variant === "unstyled";
    const chakraVariant =
      variant === "unstyled"
        ? undefined
        : (variant as ChakraIconButtonProps["variant"]);

    return (
      <ChakraIconButton
        ref={ref}
        loading={isLoading ?? props.loading}
        disabled={isDisabled ?? props.disabled}
        borderRadius={borderRadiusValue}
        unstyled={unstyledValue}
        variant={chakraVariant}
        {...props}
      >
        {icon ?? children}
      </ChakraIconButton>
    );
  }
);

export type IconButtonProps = IconButtonSharedProps &
  Omit<
    ChakraIconButtonProps,
    "children" | "colorPalette" | "disabled" | "loading" | "variant"
  > & {
    /**
     * v1/v2 compatibility (removed in v3): use children internally
     */
    icon?: ReactElement;
  };

IconButton.Solid = Solid;
IconButton.Outline = Outline;
IconButton.Ghost = Ghost;
IconButton.Primitive = Primitive;

export default IconButton;
